import React, { useState, useEffect, useRef, useCallback } from 'react';
import {Container, Table, Spinner, Alert, Badge, Button, Form, InputGroup, Card} from 'react-bootstrap';
import AppNavbar from './Navbar';
import FeedModal from './FeedModal';
import { supabaseDataService } from '../services/supabaseService';
import { useAuth } from '../contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch,
    faEye,
    faFilter,
    faSyncAlt,
    faDownload,
    faCalendarAlt,
    faSort, faSortUp, faSortDown
} from '@fortawesome/free-solid-svg-icons';
import { useDevice } from '../contexts/DeviceContext';
import _ from "lodash";
import FeedItem from "@/components/FeedItem.jsx";
import { eventBus, EVENTS } from '../utils/events';

const Feeds = () => {
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [selectedFeed, setSelectedFeed] = useState(null);
    const { isMobile, isTablet, isDesktop, windowWidth } = useDevice();
    const { user } = useAuth();

    // Add pagination state
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [fetchingMore, setFetchingMore] = useState(false);
    const itemsPerPage = 7;

    // Create a ref for the observer
    const observer = useRef();

    // Create a ref for the last feed item element
    const lastFeedElementRef = useCallback(node => {
        // Clear any existing observer
        if (observer.current) observer.current.disconnect();

        // Don't observe if we're already loading more or there's nothing more to load
        if (fetchingMore || !hasMore) return;

        // Set up the new observer if we have a node to observe
        if (node) {
            observer.current = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    loadMoreFeeds();
                }
            }, {
                // Add rootMargin to trigger loading a bit earlier
                rootMargin: '100px'
            });

            observer.current.observe(node);
        }
    }, [fetchingMore, hasMore]); // Remove loading from dependency array

    useEffect(() => {
        fetchFeeds();

        // Listen for memory approval events
        const handleMemoryApproved = (event) => {
            console.log('Memory approved, refreshing feeds:', event.detail);
            fetchFeeds(); // Refresh the feeds
        };
        const handleMemoryDiscarded = (event) => {
            console.log('Memory discarded:', event.detail);
            // You might want to refresh here too, or handle differently
            fetchFeeds();
        };
        // Add event listeners
        eventBus.on(EVENTS.MEMORY_APPROVED, handleMemoryApproved);
        eventBus.on(EVENTS.MEMORY_DISCARDED, handleMemoryDiscarded);

        // Clean up observer on component unmount
        return () => {
            eventBus.off(EVENTS.MEMORY_APPROVED, handleMemoryApproved);
            eventBus.off(EVENTS.MEMORY_DISCARDED, handleMemoryDiscarded);
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, []);

    const fetchFeeds = async () => {
        try {
            setLoading(true);
            const data = await supabaseDataService.getFeeds(user.id, 0, itemsPerPage - 1);
            setFeeds(data);
            setPage(0);
            setHasMore(data.length === itemsPerPage);
            setError('');
        } catch (err) {
            setError('Failed to load feeds. Please try again later.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const loadMoreFeeds = async () => {
        if (!hasMore || fetchingMore) return;

        try {
            setFetchingMore(true);
            const nextPage = page + 1;

            const nextFrom = nextPage * itemsPerPage;
            const nextTo = nextFrom + itemsPerPage - 1;

            const data = await supabaseDataService.getFeeds(user.id, nextFrom, nextTo);

            if (data.length > 0) {
                setFeeds(prevFeeds => [...prevFeeds, ...data]);
                setPage(nextPage);
                setHasMore(data.length === itemsPerPage);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error loading more feeds:', err);
        } finally {
            setFetchingMore(false);
        }
    };

    // Manual load more button (as a fallback)
    const handleLoadMore = () => {
        loadMoreFeeds();
    };

    if (loading && feeds.length === 0) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    return (
        <Card className="dashboard-feed dashboard-right-card">
            <Card.Body style={{ overflowY: 'auto', padding: '0' }}>
                {/* REMINDERS Header */}
                <div className="text-center pt-4 pb-3">
                    <p style={{
                        color: '#07BCD9',
                        fontSize: '12px',
                        fontWeight: '600',
                        letterSpacing: '3px',
                        margin: 0
                    }}>REMINDERS</p>
                </div>
                <div style={{ padding: '0 16px 16px' }}>
                {feeds.map((feed, index) => {
                    // Only apply the ref to the last item if there might be more items to load
                    const isLastItem = index === feeds.length - 1;

                    return (
                        <div
                            key={feed.feed_id}
                            ref={isLastItem && hasMore ? lastFeedElementRef : null}
                        >
                            <FeedItem feedItem={feed}/>
                        </div>
                    );
                })}

                {/* Loading indicator for infinite scroll */}
                {fetchingMore && (
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" role="status">
                            <span className="visually-hidden">Loading more...</span>
                        </Spinner>
                    </div>
                )}

                {/* No more items message */}
                {!hasMore && feeds.length > 0 && (
                    <div className="text-center text-muted py-3">
                        No more items to load
                    </div>
                )}

                {/* Error message */}
                {error && (
                    <Alert variant="danger" className="mt-3">
                        {error}
                    </Alert>
                )}

                {/* Manual load more button (fallback) */}
                {hasMore && !fetchingMore && feeds.length > 0 && (
                    <div className="text-center mt-3">
                        <Button variant="outline-primary" onClick={handleLoadMore}>
                            Load More
                        </Button>
                    </div>
                )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default Feeds;
